/**
 * GLAMRDiP cart drawer — 1:1 port of Bolt CartDrawer.tsx.
 *
 * Behaviour preserved from the previous implementation:
 *   - Opens on `gd:cart-open` event (dispatched from header cart button + kit-builder ATC)
 *   - Also opens on `product-added-to-cart` (standard Shopify event emitted by product forms)
 *   - AJAX endpoints: /cart.js, /cart/change.js, /discount/:code
 *   - Bundle line items (kit parent + _addon children) render as individual rows
 *   - Free-shipping threshold read from the [data-gd-cart-progress] element (fed by
 *     settings.free_shipping_threshold in the Liquid template)
 *
 * New in this port:
 *   - Bolt's bordered line-item row layout with qty stepper + trash icon
 *   - Bolt's "You're $X from FREE Shipping!" / "You've unlocked FREE Shipping!" progress copy
 *   - Subtotal + Shipping cost rows rendered separately from Total
 *   - Social-proof stats block (hidden when cart empty)
 *   - Discount code toggle reveals inline input
 *   - Empty-state swap (text + shop-now CTA vs item list)
 */
(() => {
  const drawer = document.querySelector('[data-gd-cart-drawer]');
  if (!drawer) return;

  const panel       = drawer.querySelector('.gd-cart__panel');
  const itemsHost   = drawer.querySelector('[data-gd-cart-items]');
  const tpl         = drawer.querySelector('[data-gd-cart-item-template]');
  const tplKit      = drawer.querySelector('[data-gd-cart-kit-template]');
  const tplKitChild = drawer.querySelector('[data-gd-cart-kit-child-template]');
  const emptyEl     = drawer.querySelector('[data-gd-cart-empty]');

  const progressEl   = drawer.querySelector('[data-gd-cart-progress]');
  const progressFill = drawer.querySelector('[data-gd-cart-progress-fill]');
  const progressText = drawer.querySelector('[data-gd-cart-progress-text]');
  const progressAmt  = drawer.querySelector('[data-gd-cart-progress-amt]');

  // Joel 2026-05-13: Customizer-driven progress copy (Nordic localization).
  // Templates read once from data attributes; {amount} + {label} placeholders
  // substituted per render. Empty/missing attrs fall back to current English
  // templates so unconfigured stores (UK/AU/CA) render unchanged.
  const SHIP_PENDING_TPL = (progressEl && progressEl.dataset.progressPending) ||
    "You're {amount} from {label}!";
  const SHIP_UNLOCKED_TPL = (progressEl && progressEl.dataset.progressUnlocked) ||
    "You've unlocked {label}!";
  const SHIP_LABEL = (progressEl && progressEl.dataset.freeShippingLabel) ||
    'FREE Shipping';
  // Joel 2026-05-15: Customizer-driven free-gift line labels (Nordic
  // localization). Read once at hydrate from data attrs on the drawer
  // root. Missing/empty falls back to hardcoded 'FREE'.
  const FREE_SUBTITLE_LABEL = (drawer && drawer.dataset.freeSubtitleLabel) || 'FREE';
  const _escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) =>
    ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]));

  const totalsWrap  = drawer.querySelector('[data-gd-cart-totals]');
  const subtotalEl  = drawer.querySelector('[data-gd-cart-subtotal]');
  const shippingEl  = drawer.querySelector('[data-gd-cart-shipping]');
  const headingCount = drawer.querySelector('[data-gd-cart-heading-count]');
  // Joel 2026-05-02: savings refs — markup was added to cart-drawer.liquid
  // (line ~212) when the totals were condensed, but the JS wiring never
  // landed. Computed below from cart.original_total_price − cart.total_price
  // (equivalent to cart.total_discount on standard Shopify carts).
  const savingsWrap = drawer.querySelector('[data-gd-cart-savings]');
  const savingsValEl = drawer.querySelector('[data-gd-cart-savings-val]');

  const statsWrap   = drawer.querySelector('[data-gd-cart-stats-wrap]');
  const checkoutWrap = drawer.querySelector('[data-gd-cart-checkout-wrap]');
  const totalEl     = drawer.querySelector('[data-gd-cart-total]');
  // Joel 2026-05-02 (round 9): strikethrough compare-at total ref.
  // Sibling of totalEl inside .gd-cart__total-val. JS toggles its
  // hidden attribute + textContent based on whether a compare-at
  // delta exists for this cart.
  const totalCompareEl = drawer.querySelector('[data-gd-cart-total-compare]');

  // Joel 2026-05-02 (cart drawer v3): discount-code subsystem retired.
  // Toggle button + inline form + message paragraph all removed from
  // the markup; their refs and submit handler removed below.
  // applyDiscount() helper preserved further down — it's still callable
  // from other surfaces (e.g. the cart page) that want to apply a code.

  const threshold = parseInt(progressEl?.dataset.threshold, 10) || 10000;

  let hydrated = false;
  let currentCart = null;

  const fmt = (cents) => {
    const val = (cents || 0) / 100;
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);
  };

  const open = async () => {
    // Joel 2026-04-30 (scroll-lock chain fix): make open() idempotent.
    // Both `gd:cart-open` (from finishHandoff) and `product-added-to-cart`
    // (from upsell accept) dispatch in the same accept flow, so open()
    // would otherwise run twice → lock(drawer) twice → count leaks one.
    // After the matching close → unlock pairs run, body would still be
    // locked one extra time, so subsequent modal flows see a stale
    // lock state. Skip the lock if drawer is already open.
    const wasOpen = !drawer.hidden;
    drawer.hidden = false;
    if (!wasOpen) {
      if (window.gdScrollLock) window.gdScrollLock.lock(drawer);
      else document.body.style.overflow = 'hidden';
    }
    if (!hydrated) {
      await refresh();
      hydrated = true;
    }
    drawer.querySelector('[data-gd-cart-close]')?.focus();
  };
  const close = () => {
    if (drawer.hidden) return; // idempotent — match the open() guard
    drawer.hidden = true;
    if (window.gdScrollLock) window.gdScrollLock.unlock(drawer);
    else document.body.style.overflow = '';
  };

  document.body.addEventListener('gd:cart-open', open);
  document.body.addEventListener('product-added-to-cart', async () => { await refresh(); open(); });
  // Joel 2026-06-28: re-render (WITHOUT opening) when the cart changes
  // underneath an open drawer — e.g. the gift reconciler or gd-cart-sanitizer
  // removing a line via /cart/change.js. Those paths dispatch only
  // 'cart:updated' (not 'product-added-to-cart'), which the drawer previously
  // ignored, so an auto-removed gift stayed visible until reload. Guarded by
  // !drawer.hidden so it never pops the drawer open (e.g. on the /cart page) —
  // that's why we don't just reuse the 'product-added-to-cart' listener, which
  // calls open(). The header already refreshes on 'cart:updated'; this closes
  // the matching gap for the drawer.
  document.body.addEventListener('cart:updated', () => { if (!drawer.hidden) refresh(); });
  drawer.querySelectorAll('[data-gd-cart-close]').forEach(b => b.addEventListener('click', close));
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && !drawer.hidden) close(); });

  async function fetchCart() {
    const r = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    return r.ok ? r.json() : null;
  }

  // Joel 2026-05-02 (round 7 fix): Shopify's /cart.js does NOT
  // reliably surface `compare_at_price` on cart line items, and
  // `original_line_price` is computed from line discounts only —
  // NOT from the variant's compare_at_price. So a kit at €159
  // compare-at + €124 sale comes back from cart.js as:
  //   { price: 12400, original_price: 12400, compare_at_price: null,
  //     line_price: 12400, original_line_price: 12400 }
  // The strikethrough + savings line both depend on knowing the
  // compare-at delta. Fix: enrich the cart with per-variant
  // compare_at_price by fetching `/products/{handle}.js` once per
  // unique handle (handles cached for the page lifetime). Adds
  // _compare_at_unit + _compare_at_line to each cart item that
  // has a real compare-at delta. Render + savings consume those.
  // Failed fetches degrade silently — no strike, no savings, but
  // never blocks the cart from rendering at all.
  const _compareCache = new Map(); // handle -> Map(variant_id -> compare_at_cents)
  // Joel 2026-05-16: known bug — products.json returns shop-currency compare_at_price
  //  (AUD on AU shop), not Markets-converted. NZ visitor sees AUD-cents-as-NZD strike.
  //  Fix queued: skip enrichment on secondary markets via isSecondaryMarket guard.
  async function enrichWithCompareAt(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return cart;
    const itemsByHandle = new Map();
    cart.items.forEach((it) => {
      const url = it.url || '';
      const handle = (url.split('/products/')[1] || '').split('?')[0].split('#')[0].split('/')[0];
      if (!handle) return;
      if (!itemsByHandle.has(handle)) itemsByHandle.set(handle, []);
      itemsByHandle.get(handle).push(it);
    });
    await Promise.all([...itemsByHandle.keys()].map(async (handle) => {
      let varMap = _compareCache.get(handle);
      if (!varMap) {
        try {
          const r = await fetch(`/products/${handle}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
          if (!r.ok) { _compareCache.set(handle, new Map()); return; }
          const prod = await r.json();
          varMap = new Map();
          (prod.variants || []).forEach((v) => {
            // /products/{handle}.js returns prices as INTEGER CENTS
            // (e.g. 13900 for $139.00) — same convention as /cart.js.
            // Round 7 originally multiplied by 100 thinking it returned
            // dollar strings; that's products.json (different endpoint).
            // Curl-verified via /products/instant-gels-bundle.js: numeric
            // cents already. No conversion needed.
            const cap = parseInt(v.compare_at_price, 10);
            const px = parseInt(v.price, 10);
            if (cap && cap > px) {
              varMap.set(v.id, cap);
            }
          });
          _compareCache.set(handle, varMap);
        } catch (e) {
          _compareCache.set(handle, new Map());
          return;
        }
      }
      itemsByHandle.get(handle).forEach((it) => {
        const cmpUnit = varMap.get(it.variant_id);
        if (cmpUnit && cmpUnit > (it.price || 0)) {
          it._compare_at_unit = cmpUnit;
          it._compare_at_line = cmpUnit * (it.quantity || 1);
        }
      });
    }));
    // Recompute cart-level totals if Shopify's original_total_price didn't
    // capture the compare-at delta. Sum the per-line compare delta and add
    // it onto cart.total_price so the savings line can render off the
    // existing (cart.original_total_price - cart.total_price) calc without
    // any further branching downstream.
    const cartLevelDelta = (cart.original_total_price || 0) - (cart.total_price || 0);
    if (cartLevelDelta <= 0) {
      let perLineDelta = 0;
      cart.items.forEach((it) => {
        if (it._compare_at_line && it.line_price) {
          perLineDelta += Math.max(0, it._compare_at_line - it.line_price);
        }
      });
      if (perLineDelta > 0) {
        cart._compare_at_total_delta = perLineDelta;
      }
    }
    return cart;
  }

  async function changeLine(line, quantity) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    return r.ok ? r.json() : null;
  }

  async function applyDiscount(code) {
    // Shopify's /discount/X endpoint is the storefront way; applies via session + cookie.
    // Using redirect=/cart.js to get JSON back instead of an HTML redirect.
    const r = await fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart.js`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    return r.ok;
  }

  // Strip "#NNN" suffix from kit-child catalog titles so customers see clean
  // colour names. Falls back to the title as-is when there's no #-segment.
  // (Liquid metafields aren't visible to /cart.js, so JS uses the
  // split-on-" #" rule alone — cart-page Liquid uses the metafield first.)
  function cleanName(item) {
    return (item.product_title || '').split(' #')[0].trim() || (item.product_title || '');
  }
  function isKitParent(item) { return item.properties && item.properties._kit === 'true'; }
  function isKitChild(item)  { return item.properties && item.properties._addon === 'true'; }
  function isFreeGift(item)  { return item.properties && item.properties._free_gift === 'true'; }
  function bundleId(item)    { return item.properties && item.properties.__bundle_id; }

  function render(cart) {
    currentCart = cart;
    const isEmpty = !cart || cart.item_count === 0;

    // Clear existing rendered items
    [...itemsHost.querySelectorAll('[data-gd-cart-item]')].forEach(n => n.remove());

    // Joel 2026-04-30 (P0 cart fix): null-guard each ref. The cart-drawer
    // Liquid template has been refactored over time and `data-gd-cart-totals`
    // no longer exists in markup (totals row was relocated inline), so
    // `totalsWrap` is null at runtime. The unguarded setter threw
    // "Cannot set properties of null (setting 'hidden')" inside render(),
    // which aborted the entire item-rendering loop — drawer opened but
    // showed nothing. Items WERE in the cart (cart.js confirmed), they
    // just never reached the DOM. Null-guarding restores rendering AND
    // makes the render() resilient to future template tweaks that drop
    // any of these wrappers.
    if (isEmpty) {
      if (emptyEl)     emptyEl.hidden = false;
      if (totalsWrap)  totalsWrap.hidden = true;
      if (statsWrap)   statsWrap.hidden = true;
      if (checkoutWrap) checkoutWrap.hidden = true;
      // Joel 2026-05-02: hide savings line when cart is empty so a
      // previously-shown savings amount doesn't linger after the user
      // removes the last item.
      if (savingsWrap) savingsWrap.hidden = true;
    } else {
      if (emptyEl)     emptyEl.hidden = true;
      if (totalsWrap)  totalsWrap.hidden = false;
      if (statsWrap)   statsWrap.hidden = false;
      if (checkoutWrap) checkoutWrap.hidden = false;

      // Pre-pass: build a __bundle_id → [child line indices] map so the
      // kit-parent renderer can splice children in nested form.
      const childrenByBundle = new Map();
      cart.items.forEach((item, i) => {
        if (!isKitChild(item)) return;
        const bid = bundleId(item);
        if (!bid) return;
        if (!childrenByBundle.has(bid)) childrenByBundle.set(bid, []);
        childrenByBundle.get(bid).push({ item, line: i + 1 });
      });

      cart.items.forEach((item, i) => {
        // Kit children render nested inside the parent — skip at top level.
        if (isKitChild(item)) return;

        const lineNum = i + 1;
        let node;
        if (isKitParent(item) && tplKit) {
          node = tplKit.content.firstElementChild.cloneNode(true);
          node.dataset.bundleId = bundleId(item) || '';
          node.dataset.line = String(lineNum);
          // Wire children — pull from the pre-pass map.
          const childList = node.querySelector('[data-gd-cart-kit-children]');
          const bid = bundleId(item);
          const childRows = bid ? (childrenByBundle.get(bid) || []) : [];
          // Stable order: children render in the same order Shopify returned
          // them (insertion = ATC order from kit-builder.js).
          if (tplKitChild && childList) {
            childRows.forEach(({ item: child }) => {
              const cn = tplKitChild.content.firstElementChild.cloneNode(true);
              const cImg = cn.querySelector('[data-gd-cart-img]');
              const cImgSrc = child.featured_image?.url || child.image;
              if (cImgSrc && cImg) { cImg.src = cImgSrc; cImg.alt = cleanName(child); }
              else if (cImg) cImg.remove();
              const titleEl = cn.querySelector('[data-gd-cart-title]');
              if (titleEl) titleEl.textContent = cleanName(child);
              childList.appendChild(cn);
            });
          }
          // Track child line numbers on the parent so the remove handler can
          // batch-zero them in /cart/update.js.
          node.dataset.childLines = JSON.stringify(childRows.map(r => r.line));
        } else {
          node = tpl.content.firstElementChild.cloneNode(true);
          node.dataset.line = String(lineNum);
        }

        // Common fill — link, image, title (cleaned), price, variant.
        // Joel 2026-05-02 (Phase 2): for free-gift line items, do NOT
        // link to the PDP — replace each <a data-gd-cart-link> with a
        // <span> so the title + image render but aren't clickable.
        // Reason: gift product PDP exists for "what is this?" curiosity
        // (template suffix `gift` renders an info-only fallback with no
        // ATC), but the cart-drawer click was the most likely path
        // users would notice and try. Suppressing the link keeps the
        // gift contained to the auto-add-via-rule flow.
        if (isFreeGift(item)) {
          node.querySelectorAll('[data-gd-cart-link]').forEach((a) => {
            const span = document.createElement('span');
            // Carry over all relevant attributes except href.
            for (const attr of a.attributes) {
              if (attr.name === 'href') continue;
              span.setAttribute(attr.name, attr.value);
            }
            // Preserve children (title text gets set later by the
            // [data-gd-cart-title] textContent write further below).
            while (a.firstChild) span.appendChild(a.firstChild);
            a.replaceWith(span);
          });
        } else {
          node.querySelectorAll('[data-gd-cart-link]').forEach((a) => { a.href = item.url; });
        }
        const img = node.querySelector('[data-gd-cart-img]');
        const imgSrc = item.featured_image?.url || item.image;
        if (imgSrc && img) { img.src = imgSrc; img.alt = item.product_title || ''; }
        else if (img) img.remove();
        node.querySelector('[data-gd-cart-title]').textContent = cleanName(item);
        // Joel 2026-05-02 (round 7): price block with optional compare-at
        // strike. Spec: render `original_line_price` strikethrough next to
        // the sale `line_price` when they differ. Falls back to the simple
        // single-price string when there is no compare-at delta. The
        // template clones come pre-wired with both child spans + the
        // .gd-price utility class on the parent. We just toggle the
        // `--has-compare` modifier + the [hidden] attribute on the strike
        // span. textContent on legacy `[data-gd-cart-price]` is no longer
        // set directly because the element is now a flex container — its
        // children carry the text. The legacy single-line write path
        // (one .textContent on the wrapper) would clobber the child
        // spans and lose the strike — DO NOT re-introduce it.
        const priceEl = node.querySelector('[data-gd-cart-price]');
        const priceCompareEl = node.querySelector('[data-gd-cart-price-compare]');
        const priceSaleEl = node.querySelector('[data-gd-cart-price-sale]');
        const lineSale = item.final_line_price ?? item.line_price ?? item.final_price;
        // Joel 2026-05-02 (round 7): prefer the enrichment-sourced
        // compare-at line total when available (Shopify cart.js
        // doesn't reliably populate `original_line_price` from the
        // variant's compare_at_price). Falls back to original_line_price
        // if it actually differs from line_price (e.g. discount-code
        // active), otherwise no compare to render.
        const lineCompare = item._compare_at_line || item.original_line_price || lineSale;
        if (priceSaleEl) {
          priceSaleEl.textContent = fmt(lineSale);
        } else if (priceEl) {
          // Defensive fallback: if a stale template instance is missing
          // the new spans (e.g. cached old template clone), still write
          // the single price so the cart isn't price-less.
          priceEl.textContent = fmt(lineSale);
        }
        if (priceEl && priceCompareEl) {
          if (lineCompare > lineSale) {
            priceCompareEl.textContent = fmt(lineCompare);
            priceCompareEl.hidden = false;
            priceEl.classList.add('gd-price--has-compare');
            priceSaleEl?.classList.add('gd-price__sale');
          } else {
            priceCompareEl.hidden = true;
            priceEl.classList.remove('gd-price--has-compare');
            priceSaleEl?.classList.remove('gd-price__sale');
          }
        }
        const variantEl = node.querySelector('[data-gd-cart-variant]');
        if (variantEl && item.variant_title && item.variant_title !== 'Default Title') {
          variantEl.textContent = item.variant_title;
          variantEl.hidden = false;
        }
        const qtyEl = node.querySelector('[data-gd-cart-qty]');
        if (qtyEl) qtyEl.textContent = item.quantity;

        // Joel 2026-05-02: free-gift line treatment. When line has
        // `_free_gift: 'true'` property: add modifier class for CSS,
        // hide qty stepper + remove button (gift is system-managed),
        // and overwrite price block with bold "FREE" plus optional
        // compare-at strike if Shopify exposes it. Image link kept
        // active so users can still see what they're getting (the
        // PDP itself uses template suffix `gift` to render a fallback
        // — no ATC, no purchase path).
        if (isFreeGift(item)) {
          node.classList.add('gd-cart__item--gift');
          // Joel 2026-05-15: unhide the Customizer-driven FREE pill badge.
          // Text content already rendered server-side from
          // section.settings.free_badge_label via the template <span>.
          const freeBadgeEl = node.querySelector('[data-gd-cart-free-badge]');
          if (freeBadgeEl) freeBadgeEl.hidden = false;
          // Strip the qty stepper container entirely
          const qtyContainer = node.querySelector('.gd-cart__qty');
          if (qtyContainer) qtyContainer.remove();
          // Strip the trash remove button
          const removeBtn = node.querySelector('[data-gd-cart-remove]');
          if (removeBtn) removeBtn.remove();
          // Replace price-sale text with the Customizer-driven label —
          // keep compare-at if any (Shopify carries variant.compare_at_price
          // through to line.original_line_price for $0 variants when admin
          // set the field). Sale span gets the brand-violet emphasis.
          if (priceSaleEl) {
            priceSaleEl.textContent = FREE_SUBTITLE_LABEL;
            priceSaleEl.classList.add('gd-price__sale');
          }
          // Joel 2026-05-02: caption is now per-rule editable. Each
          // reward line carries _gift_caption (set by gd-gift-with-purchase.js
          // from the matching rule's `caption` block setting). Falls back
          // to the literal default for: pre-existing gifts in cart from
          // before this fix shipped, OR rules where the merchant left
          // the caption setting empty.
          if (variantEl) {
            const giftCaption = (item.properties && item.properties._gift_caption);
            variantEl.textContent = (giftCaption && String(giftCaption).trim())
              || 'Gift with purchase — included free';
            variantEl.hidden = false;
            variantEl.classList.add('gd-cart__item-gift-label');
          }
        }
        itemsHost.appendChild(node);
      });

      // Joel 2026-04-30 (P0 cart fix): null-guard each totals ref. The
      // template no longer renders [data-gd-cart-subtotal] or
      // [data-gd-cart-shipping] (totals row condensed into a single
      // [data-gd-cart-total]); the legacy refs return null and the
      // unguarded textContent setters threw mid-render.
      // Joel 2026-05-02 (cart drawer v2): Subtotal + Shipping rows
      // retired from the footer. The legacy refs return null at runtime
      // (markup is gone) — the null-guards keep this resilient if anyone
      // re-introduces the rows later. Total is now the only money line.
      if (subtotalEl) subtotalEl.textContent = fmt(cart.total_price);
      if (shippingEl) shippingEl.textContent = cart.total_price >= threshold ? '$0.00' : '$0.00';
      if (totalEl)    totalEl.textContent    = fmt(cart.total_price);

      // Joel 2026-05-02 (round 9): Total-row strikethrough. Same
      // compare-at delta source the Savings line uses (see below):
      // prefer Shopify's native original_total_price when it differs,
      // otherwise the per-line enrichment fallback. fmt() converts
      // cents → display $; same path as line-item strikethrough so
      // the formats match exactly. Hidden when the cart has no delta.
      if (totalCompareEl) {
        const cartLevelDeltaForTotal = (cart.original_total_price || 0) - (cart.total_price || 0);
        const enrichedDelta = cart._compare_at_total_delta || 0;
        const compareTotal = cartLevelDeltaForTotal > 0
          ? cart.original_total_price
          : (enrichedDelta > 0 ? cart.total_price + enrichedDelta : 0);
        if (compareTotal > 0 && compareTotal > cart.total_price) {
          totalCompareEl.textContent = fmt(compareTotal);
          totalCompareEl.hidden = false;
        } else {
          totalCompareEl.hidden = true;
        }
      }

      // Joel 2026-05-02 (cart drawer v2): savings calculation.
      // Use original_total_price - total_price to capture BOTH compare-at
      // delta AND discount-code savings. Don't "simplify" to
      // cart.total_discount — that field only reports discount-code value
      // and misses the dominant compare-at savings source on this store
      // (kit + bundle line items all carry compare_at_price > price).
      // Display: "$X" (no leading minus) — the bar header reads "You
      // saved $X" so the value is positive in copy.
      // Joel 2026-05-02 (round 7): when Shopify's cart-level
      // original_total_price doesn't expose the compare-at delta
      // (see enrichWithCompareAt comment), fall back to the
      // per-line sum we computed from /products/{handle}.js.
      const cartLevelSavings = (cart.original_total_price || 0) - (cart.total_price || 0);
      const savings = cartLevelSavings > 0
        ? cartLevelSavings
        : (cart._compare_at_total_delta || 0);
      if (savings > 0 && savingsValEl) {
        savingsValEl.textContent = fmt(savings);
      }
      if (savingsWrap) {
        savingsWrap.hidden = !(savings > 0);
      }
    }

    // Joel 2026-05-02 (round 4): heading count must filter out kit
    // children (`_addon: 'true'` line items) so the badge reads
    // "Your cart (1)" for a kit-only cart instead of "(3)" (kit
    // parent + 2 bundled colours). Empty cart → no parenthetical
    // at all (`{% if visible_count > 0 %}` in Liquid; same logic
    // here for live updates after refresh()).
    let visibleCount = 0;
    if (cart && cart.items) {
      cart.items.forEach(it => {
        if (it.properties && it.properties._addon === 'true') return;
        visibleCount += it.quantity || 0;
      });
    }
    if (headingCount) {
      headingCount.textContent = visibleCount > 0 ? ` (${visibleCount})` : '';
    }
    console.log('[gd-cart-drawer count diag]', {
      raw_item_count: cart && cart.item_count,
      visible_count: visibleCount,
      items_with_props: cart && cart.items && cart.items.map(i => ({
        title: i.product_title,
        qty: i.quantity,
        properties: i.properties,
      })),
    });

    // Joel 2026-05-02: free-gift BANNER kit-only toggle. Pure
    // visibility — no cart manipulation. When the banner element has
    // data-gd-gift-banner-kit-only="true", hide it unless the cart
    // contains at least one line with `_kit: 'true'` property. When
    // the toggle is off / missing, banner always shows (CSS default).
    const giftBanner = drawer.querySelector('[data-gd-cart-gift-banner]');
    if (giftBanner && giftBanner.dataset.gdGiftBannerKitOnly === 'true') {
      const hasKitLine = cart && cart.items && cart.items.some(isKitParent);
      giftBanner.hidden = !hasKitLine;
    }

    // Progress bar + threshold message
    const totalCents = cart?.total_price || 0;
    const pct = Math.min(100, Math.round((totalCents / threshold) * 100));
    if (progressFill) {
      // Belt-and-braces: inline `display: block` so Dawn's base.css rule
      // `div:empty { display: none; }` (specificity 0,0,1,1) can never
      // collapse the bar to 0×0, even if the cascade-bump rule in
      // sections/cart-drawer.liquid is somehow lost (e.g. minified-CSS
      // edge case in editor preview).
      progressFill.style.display = 'block';
      progressFill.style.width = pct + '%';
    }
    if (progressText) {
      const labelHTML = `<span class="gd-cart__ship-bold">${_escapeHtml(SHIP_LABEL)}</span>`;
      if (totalCents >= threshold) {
        progressText.innerHTML = _escapeHtml(SHIP_UNLOCKED_TPL).replace('{label}', labelHTML);
      } else {
        const remaining = Math.max(0, threshold - totalCents);
        const amountHTML = `<span class="gd-cart__ship-amt">${fmt(remaining)}</span>`;
        progressText.innerHTML = _escapeHtml(SHIP_PENDING_TPL)
          .replace('{amount}', amountHTML)
          .replace('{label}', labelHTML);
      }
    }
  }

  // Joel 2026-05-15: post-enrichment DOM upgrade. Called after
  // enrichWithCompareAt() populates _compare_at_unit / _compare_at_line
  // on cart.items + _compare_at_total_delta on cart. Updates ONLY the
  // price strikethrough + savings + total-compare spots — line items
  // themselves were already rendered by render() with sale prices.
  // Mirrors the compare-handling branches of render() in shape; keep
  // the two in sync if compare-display rules change. (Future tech-debt:
  // extract the shared compare-handler into a single function — out of
  // scope for the perf fix.)
  function upgradeWithCompareAt(cart) {
    if (!cart || !cart.items) return;

    // Per-line strikethrough
    cart.items.forEach((item, i) => {
      if (!item._compare_at_line) return;
      if (isKitChild(item)) return; // kit children share parent's price slot
      const lineNum = i + 1;
      const node = itemsHost.querySelector(`[data-gd-cart-item][data-line="${lineNum}"]`);
      if (!node) return;
      const priceEl = node.querySelector('[data-gd-cart-price]');
      const priceCompareEl = node.querySelector('[data-gd-cart-price-compare]');
      const priceSaleEl = node.querySelector('[data-gd-cart-price-sale]');
      const lineSale = item.final_line_price ?? item.line_price ?? item.final_price;
      const lineCompare = item._compare_at_line;
      if (priceEl && priceCompareEl && lineCompare > lineSale) {
        priceCompareEl.textContent = fmt(lineCompare);
        priceCompareEl.hidden = false;
        priceEl.classList.add('gd-price--has-compare');
        priceSaleEl?.classList.add('gd-price__sale');
      }
    });

    // Cart-level total-row strikethrough
    if (totalCompareEl) {
      const cartLevelDeltaForTotal = (cart.original_total_price || 0) - (cart.total_price || 0);
      const enrichedDelta = cart._compare_at_total_delta || 0;
      const compareTotal = cartLevelDeltaForTotal > 0
        ? cart.original_total_price
        : (enrichedDelta > 0 ? cart.total_price + enrichedDelta : 0);
      if (compareTotal > 0 && compareTotal > cart.total_price) {
        totalCompareEl.textContent = fmt(compareTotal);
        totalCompareEl.hidden = false;
      }
    }

    // Savings row (cart-level)
    const cartLevelSavings = (cart.original_total_price || 0) - (cart.total_price || 0);
    const savings = cartLevelSavings > 0 ? cartLevelSavings : (cart._compare_at_total_delta || 0);
    if (savings > 0 && savingsValEl) {
      savingsValEl.textContent = fmt(savings);
    }
    if (savingsWrap) {
      savingsWrap.hidden = !(savings > 0);
    }
  }

  async function refresh() {
    const cart = await fetchCart();
    if (!cart) return;

    // Joel 2026-05-15: progressive enhancement. Render line items
    // immediately from /cart.js, then enrich compare-at via
    // per-product fetches in the background. When enrichment
    // resolves, upgradeWithCompareAt() updates JUST the strikethrough
    // + savings spots in place — no full re-render, no layout shift.
    // Warm-cache path (subsequent opens for products already in
    // _compareCache) resolves synchronously, so the upgrade fires
    // effectively immediately — same UX as the old await-first model.
    // Failure mode: line items already rendered, savings/strike just
    // stays absent — graceful degradation. See enrichWithCompareAt
    // comment above for context on why per-product enrichment exists.
    render(cart);
    document.body.dispatchEvent(new CustomEvent('gd:cart-refreshed', { detail: cart }));

    drawer.classList.add('is-enriching');
    enrichWithCompareAt(cart)
      .then(() => upgradeWithCompareAt(cart))
      .catch((err) => {
        console.warn('[gd-cart-drawer] compare-at enrichment failed; line items render without strikethrough', err);
      })
      .finally(() => drawer.classList.remove('is-enriching'));
  }

  // POST /cart/update.js with line-keyed quantities. Use this when removing
  // a kit parent so the parent + all children disappear in one round-trip
  // (otherwise sequential changeLine calls re-index lines mid-flight).
  async function updateLines(updatesByLine) {
    const r = await fetch('/cart/update.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updatesByLine }),
    });
    return r.ok ? r.json() : null;
  }

  // Delegated qty + remove handlers — attached to document so they
  // survive any DOM replacement of the cart drawer / items host
  // (Shopify section:load can swap the section; listener on the host
  // would orphan onto a detached node).
  document.addEventListener('click', async (e) => {
    if (!e.target.closest('[data-gd-cart-drawer]')) return;
    const item = e.target.closest('[data-gd-cart-item]');
    if (!item) return;
    const line = parseInt(item.dataset.line, 10);
    const qtyEl = item.querySelector('[data-gd-cart-qty]');
    const currentQty = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;

    if (e.target.closest('[data-gd-cart-inc]')) {
      e.preventDefault();
      await changeLine(line, currentQty + 1);
      await refresh();
    } else if (e.target.closest('[data-gd-cart-dec]')) {
      e.preventDefault();
      if (currentQty > 1) {
        await changeLine(line, currentQty - 1);
      } else {
        await changeLine(line, 0);
      }
      await refresh();
    } else if (e.target.closest('[data-gd-cart-remove]')) {
      e.preventDefault();
      const removeBtn = e.target.closest('[data-gd-cart-remove]');
      // Joel 2026-05-02 (cart drawer v3): aria-busy is set on the
      // ITEM (not the button) so the CSS in cart-drawer.liquid can
      // dim the entire row + overlay a centred spinner during the
      // /cart/change.js or /cart/update.js call. Disable the button
      // so a frantic double-click can't queue a second remove.
      item.setAttribute('aria-busy', 'true');
      removeBtn.disabled = true;
      try {
        // Kit parent? Build a sparse line→0 update covering parent + children.
        // /cart/update.js requires a positional array of length cart.items —
        // entries we don't touch stay at their current quantity.
        const isKitParent = item.hasAttribute('data-gd-cart-kit-parent');
        let childLines = [];
        try { childLines = JSON.parse(item.dataset.childLines || '[]'); } catch {}
        if (isKitParent && childLines.length > 0 && currentCart) {
          const total = currentCart.items.length;
          const updates = new Array(total);
          for (let i = 0; i < total; i++) updates[i] = currentCart.items[i].quantity;
          // Zero parent + every linked child line
          const zeros = new Set([line, ...childLines]);
          zeros.forEach(L => { if (L >= 1 && L <= total) updates[L - 1] = 0; });
          await updateLines(updates);
        } else {
          await changeLine(line, 0);
        }
        await refresh();
      } catch (err) {
        // On error, clear the spinner state so the user can retry.
        // refresh() above re-renders the row from the latest cart
        // state, so on success the spinner-bearing item has already
        // been replaced with a fresh node — no need to reset aria-busy
        // in the success path. Reset only fires on the catch branch.
        item.removeAttribute('aria-busy');
        removeBtn.disabled = false;
        console.error('[gd-cart-drawer] remove failed:', err);
      }
    }
  });
})();
